import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../AuthContext';
import {
  Drawer,
  Select,
  Switch,
  Button,
  Statistic,
  Spin,
  Typography,
  Tag,
  message,
  Input,
  Divider,
} from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

const { Text } = Typography;
const API_URL = process.env.REACT_APP_API_URL;

const CLIENT_OPTIONS = [
  { value: 'all', label: 'Tous' },
  { value: 'only_client', label: 'Clients uniquement' },
  { value: 'only_super', label: 'Super clients uniquement' },
  { value: 'exclude_client', label: 'Exclure clients (+ super)' },
  { value: 'exclude_super', label: 'Exclure super clients' },
];

export default function GestionPlaylistDrawer({
  open,
  onClose,
  onSaved,
  playlist, // null = création, objet = édition
  tags,
  activites,
  regions,
  departements,
}) {
  const { user } = useAuth();
  const isEdit = !!playlist;

  const [name, setName] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [selectedActiviteIds, setSelectedActiviteIds] = useState([]);
  const [selectedRegionIds, setSelectedRegionIds] = useState([]);
  const [selectedDeptIds, setSelectedDeptIds] = useState([]);
  const [clientFilter, setClientFilter] = useState('exclude_client');
  const [excludeBloque, setExcludeBloque] = useState(true);
  const [excludeLr, setExcludeLr] = useState(true);
  const [onlyHomeLogo, setOnlyHomeLogo] = useState(false);

  const [count, setCount] = useState(null);
  const [countLoading, setCountLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const debounceRef = useRef(null);
  const token = localStorage.getItem('prae.jwt');
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  // Pré-remplir en mode édition
  useEffect(() => {
    if (!open) return;
    if (isEdit && playlist) {
      setName(playlist.name || '');
      const f =
        typeof playlist.gestion_filters === 'string'
          ? JSON.parse(playlist.gestion_filters)
          : playlist.gestion_filters || {};
      setSelectedTagIds(f.tag_ids || []);
      setSelectedActiviteIds(f.activite_ids || []);
      setSelectedDeptIds(f.departement_ids || []);
      setClientFilter(f.client_filter || 'all');
      setExcludeBloque(f.exclude_bloque !== false);
      setExcludeLr(f.exclude_lr !== false);
      setOnlyHomeLogo(f.only_home_logo === true);
      // Retrouver region IDs depuis num_region
      if (f.region_ids && regions.length) {
        const rIds = regions
          .filter(r => f.region_ids.includes(r.num_region))
          .map(r => r.id);
        setSelectedRegionIds(rIds);
      } else {
        setSelectedRegionIds([]);
      }
    } else {
      setName('');
      setSelectedTagIds([]);
      setSelectedActiviteIds([]);
      setSelectedRegionIds([]);
      setSelectedDeptIds([]);
      setClientFilter('exclude_client');
      setExcludeBloque(true);
      setExcludeLr(true);
      setOnlyHomeLogo(false);
      setCount(null);
    }
  }, [open, isEdit, playlist, regions]);

  const buildFilters = useCallback(() => {
    const filters = {
      exclude_bloque: excludeBloque,
      exclude_lr: excludeLr,
      client_filter: clientFilter,
    };
    if (onlyHomeLogo) filters.only_home_logo = true;
    if (selectedTagIds.length > 0) filters.tag_ids = selectedTagIds;
    if (selectedActiviteIds.length > 0)
      filters.activite_ids = selectedActiviteIds;
    if (selectedRegionIds.length > 0) {
      filters.region_ids = regions
        .filter(r => selectedRegionIds.includes(r.id))
        .map(r => r.num_region);
    }
    if (selectedDeptIds.length > 0) filters.departement_ids = selectedDeptIds;
    return filters;
  }, [
    selectedTagIds,
    selectedActiviteIds,
    selectedRegionIds,
    selectedDeptIds,
    clientFilter,
    excludeBloque,
    excludeLr,
    onlyHomeLogo,
    regions,
  ]);

  // Auto-count
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setCountLoading(true);
      try {
        const res = await fetch(`${API_URL}/playlists/gestion/count`, {
          method: 'POST',
          headers,
          body: JSON.stringify(buildFilters()),
        });
        const json = await res.json();
        setCount(json.count ?? null);
      } catch {
        setCount(null);
      } finally {
        setCountLoading(false);
      }
    }, 500);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildFilters, open]);

  const handleSave = async () => {
    if (!name.trim()) {
      message.warning('Veuillez saisir un nom');
      return;
    }
    setSaving(true);
    try {
      let res;
      if (isEdit) {
        res = await fetch(`${API_URL}/playlists/${playlist.id}/gestion`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({
            name: name.trim(),
            gestion_filters: buildFilters(),
          }),
        });
      } else {
        res = await fetch(`${API_URL}/playlists/gestion`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            name: name.trim(),
            userId: user?.id,
            gestion_filters: buildFilters(),
          }),
        });
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      message.success(isEdit ? 'Fichier mis à jour' : 'Fichier créé');
      onSaved?.();
      onClose();
    } catch (err) {
      message.error(`Erreur : ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const selectedNumRegions = regions
    .filter(r => selectedRegionIds.includes(r.id))
    .map(r => r.num_region);
  const filteredDepts =
    selectedNumRegions.length > 0
      ? departements.filter(d => selectedNumRegions.includes(d.num_region))
      : departements;

  return (
    <Drawer
      title={isEdit ? `Modifier : ${playlist?.name}` : 'Nouveau fichier'}
      open={open}
      onClose={onClose}
      width={480}
      extra={
        <Button
          type="primary"
          onClick={handleSave}
          loading={saving}
          style={{ background: '#f97316', borderColor: '#f97316' }}
        >
          {isEdit ? 'Mettre à jour' : 'Créer'}
        </Button>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Nom */}
        <div>
          <Text
            strong
            style={{ fontSize: 12, marginBottom: 4, display: 'block' }}
          >
            Nom du fichier
          </Text>
          <Input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Ex: Premium PACA"
          />
        </div>

        {/* Tags */}
        <div>
          <Text
            strong
            style={{ fontSize: 12, marginBottom: 6, display: 'block' }}
          >
            Tags
          </Text>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {tags.map(tag => {
              const selected = selectedTagIds.includes(tag.id);
              return (
                <Tag
                  key={tag.id}
                  style={{
                    cursor: 'pointer',
                    background: tag.color_bkg || '#eee',
                    color: tag.color_text || '#333',
                    border: selected
                      ? '3px solid #1e293b'
                      : '3px solid transparent',
                    fontWeight: selected ? 700 : 400,
                    fontSize: 11,
                    userSelect: 'none',
                    opacity: selected ? 1 : 0.6,
                  }}
                  onClick={() =>
                    setSelectedTagIds(prev =>
                      selected
                        ? prev.filter(id => id !== tag.id)
                        : [...prev, tag.id]
                    )
                  }
                >
                  {tag.nom}
                </Tag>
              );
            })}
          </div>
        </div>

        {/* Activité */}
        <div>
          <Text
            strong
            style={{ fontSize: 12, marginBottom: 4, display: 'block' }}
          >
            Activité
          </Text>
          <Select
            mode="multiple"
            placeholder="Toutes les activités"
            value={selectedActiviteIds}
            onChange={setSelectedActiviteIds}
            allowClear
            maxTagCount={2}
            style={{ width: '100%' }}
            options={activites.map(a => ({ value: a.id, label: a.nom }))}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </div>

        {/* Région */}
        <div>
          <Text
            strong
            style={{ fontSize: 12, marginBottom: 4, display: 'block' }}
          >
            Région
          </Text>
          <Select
            mode="multiple"
            placeholder="Toutes les régions"
            value={selectedRegionIds}
            onChange={v => {
              setSelectedRegionIds(v);
              setSelectedDeptIds([]);
            }}
            allowClear
            maxTagCount={2}
            style={{ width: '100%' }}
            options={regions.map(r => ({ value: r.id, label: r.nom }))}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </div>

        {/* Département */}
        <div>
          <Text
            strong
            style={{ fontSize: 12, marginBottom: 4, display: 'block' }}
          >
            Département
          </Text>
          <Select
            mode="multiple"
            placeholder="Tous les départements"
            value={selectedDeptIds}
            onChange={setSelectedDeptIds}
            allowClear
            maxTagCount={3}
            style={{ width: '100%' }}
            options={filteredDepts.map(d => ({
              value: d.id,
              label: `${String(d.code_departement).padStart(2, '0')} – ${d.nom}`,
            }))}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </div>

        {/* Client filter */}
        <div>
          <Text
            strong
            style={{ fontSize: 12, marginBottom: 4, display: 'block' }}
          >
            Clients
          </Text>
          <Select
            value={clientFilter}
            onChange={setClientFilter}
            style={{ width: '100%' }}
            options={CLIENT_OPTIONS}
          />
        </div>

        {/* Toggles */}
        <div style={{ display: 'flex', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Switch
              size="small"
              checked={excludeBloque}
              onChange={setExcludeBloque}
            />
            <Text style={{ fontSize: 12 }}>Exclure bloqués</Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Switch size="small" checked={excludeLr} onChange={setExcludeLr} />
            <Text style={{ fontSize: 12 }}>Exclure listes rouges</Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Switch
              size="small"
              checked={onlyHomeLogo}
              onChange={setOnlyHomeLogo}
            />
            <Text style={{ fontSize: 12 }}>Logo maison</Text>
          </div>
        </div>

        <Divider style={{ margin: '4px 0' }} />

        {/* Count */}
        <div
          style={{
            background: '#f6ffed',
            border: '1px solid #b7eb8f',
            borderRadius: 8,
            padding: '10px 14px',
          }}
        >
          {countLoading ? (
            <Spin indicator={<LoadingOutlined />} size="small" />
          ) : (
            <Statistic
              value={count ?? '–'}
              suffix="contacts"
              valueStyle={{ fontSize: 18, fontWeight: 700 }}
            />
          )}
        </div>
      </div>
    </Drawer>
  );
}
